import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const TYPE_COLORS = {
  'Distributor': 'bg-blue-100 text-blue-700',
  'Retail Store': 'bg-emerald-100 text-emerald-700',
  'Dealer': 'bg-amber-100 text-amber-700',
  'Sub-Distributor': 'bg-violet-100 text-violet-700',
};

const STATUS_COLORS = {
  'Active': 'bg-emerald-100 text-emerald-700',
  'Inactive': 'bg-slate-100 text-slate-600',
  'On Hold': 'bg-rose-100 text-rose-700',
};

export default function AccountTable({ accounts, onView, onEdit, onMap, onSort, sortConfig }) {
  const [expandedRow, setExpandedRow] = useState(null);

  const SortIcon = ({ col }) => {
    if (sortConfig?.key !== col) return <Icon name="ChevronsUpDown" size={13} color="currentColor" strokeWidth={2} />;
    return sortConfig?.dir === 'asc'
      ? <Icon name="ChevronUp" size={13} color="currentColor" strokeWidth={2} />
      : <Icon name="ChevronDown" size={13} color="currentColor" strokeWidth={2} />;
  };

  const ThBtn = ({ col, children }) => (
    <button
      className="flex items-center gap-1 font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
      onClick={() => onSort(col)}
    >
      {children}
      <SortIcon col={col} />
    </button>
  );

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="px-4 py-3 text-left"><ThBtn col="name">Account Name</ThBtn></th>
              <th className="px-4 py-3 text-left"><ThBtn col="type">Type</ThBtn></th>
              <th className="px-4 py-3 text-left"><ThBtn col="region">Region / Area</ThBtn></th>
              <th className="px-4 py-3 text-left"><ThBtn col="salesperson">Salesperson</ThBtn></th>
              <th className="px-4 py-3 text-left"><ThBtn col="creditLimit">Credit Limit</ThBtn></th>
              <th className="px-4 py-3 text-left"><ThBtn col="lastSale">Last Sale</ThBtn></th>
              <th className="px-4 py-3 text-left"><ThBtn col="status">Status</ThBtn></th>
              <th className="px-4 py-3 text-center">
                <span className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {accounts?.map((acc, idx) => (
              <tr
                key={acc?.id}
                className={`border-b border-border transition-colors hover:bg-muted/30 ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
              >
                <td className="px-4 py-3">
                  <div>
                    <p className="font-caption text-sm font-medium text-foreground">{acc?.name}</p>
                    <p className="font-caption text-xs text-muted-foreground truncate max-w-[200px]">{acc?.address}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS?.[acc?.type] || 'bg-slate-100 text-slate-600'}`}>
                    {acc?.type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <p className="font-caption text-sm text-foreground">{acc?.region}</p>
                  <p className="font-caption text-xs text-muted-foreground">{acc?.area}</p>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="font-caption text-xs font-semibold text-primary">{acc?.salesperson?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0,2)}</span>
                    </div>
                    <span className="font-caption text-sm text-foreground">{acc?.salesperson}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <p className="font-data text-sm text-foreground">₱{acc?.creditLimit?.toLocaleString()}</p>
                  <div className="mt-1 h-1 w-20 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400 rounded-full" style={{ width: `${acc?.creditUsedPct}%` }} />
                  </div>
                  <p className="font-caption text-xs text-muted-foreground">{acc?.creditUsedPct}% used</p>
                </td>
                <td className="px-4 py-3">
                  <p className="font-data text-sm text-foreground">₱{acc?.lastSaleAmount?.toLocaleString()}</p>
                  <p className="font-caption text-xs text-muted-foreground">{acc?.lastSaleDate}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS?.[acc?.status] || ''}`}>
                    {acc?.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => onView(acc)}
                      title="View Details"
                    >
                      <Icon name="Eye" size={15} color="currentColor" strokeWidth={2} />
                    </button>
                    <button
                      className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => onEdit(acc)}
                      title="Edit Account"
                    >
                      <Icon name="Pencil" size={15} color="currentColor" strokeWidth={2} />
                    </button>
                    <button
                      className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                      onClick={() => onMap(acc)}
                      title="View on Map"
                    >
                      <Icon name="MapPin" size={15} color="currentColor" strokeWidth={2} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {accounts?.length === 0 && (
          <div className="py-16 text-center">
            <Icon name="SearchX" size={40} color="var(--color-muted-foreground)" strokeWidth={1.5} />
            <p className="mt-3 font-caption text-muted-foreground">No accounts found matching your filters.</p>
          </div>
        )}
      </div>
      {/* Mobile / Tablet Card Layout */}
      <div className="lg:hidden space-y-3">
        {accounts?.map((acc) => (
          <div key={acc?.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <div
              className="p-4 cursor-pointer"
              onClick={() => setExpandedRow(expandedRow === acc?.id ? null : acc?.id)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-caption text-sm font-semibold text-foreground">{acc?.name}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS?.[acc?.type] || ''}`}>
                      {acc?.type}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS?.[acc?.status] || ''}`}>
                      {acc?.status}
                    </span>
                  </div>
                  <p className="font-caption text-xs text-muted-foreground mt-1 truncate">{acc?.address}</p>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className="font-caption text-xs text-muted-foreground flex items-center gap-1">
                      <Icon name="MapPin" size={11} color="currentColor" strokeWidth={2} />
                      {acc?.region} · {acc?.area}
                    </span>
                    <span className="font-caption text-xs text-muted-foreground flex items-center gap-1">
                      <Icon name="User" size={11} color="currentColor" strokeWidth={2} />
                      {acc?.salesperson}
                    </span>
                  </div>
                </div>
                <Icon name={expandedRow === acc?.id ? 'ChevronUp' : 'ChevronDown'} size={16} color="currentColor" strokeWidth={2} className="flex-shrink-0 mt-1" />
              </div>
            </div>

            {expandedRow === acc?.id && (
              <div className="border-t border-border px-4 py-3 bg-muted/20 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="font-caption text-xs text-muted-foreground">Credit Limit</p>
                    <p className="font-data text-sm font-medium text-foreground">₱{acc?.creditLimit?.toLocaleString()}</p>
                    <div className="mt-1 h-1 w-full bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${acc?.creditUsedPct}%` }} />
                    </div>
                    <p className="font-caption text-xs text-muted-foreground">{acc?.creditUsedPct}% used</p>
                  </div>
                  <div>
                    <p className="font-caption text-xs text-muted-foreground">Last Sale</p>
                    <p className="font-data text-sm font-medium text-foreground">₱{acc?.lastSaleAmount?.toLocaleString()}</p>
                    <p className="font-caption text-xs text-muted-foreground">{acc?.lastSaleDate}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" iconName="Eye" iconPosition="left" onClick={() => onView(acc)}>View</Button>
                  <Button variant="outline" size="sm" iconName="Pencil" iconPosition="left" onClick={() => onEdit(acc)}>Edit</Button>
                  <Button variant="outline" size="sm" iconName="MapPin" iconPosition="left" onClick={() => onMap(acc)}>Map</Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {accounts?.length === 0 && (
          <div className="py-16 text-center">
            <Icon name="SearchX" size={40} color="var(--color-muted-foreground)" strokeWidth={1.5} />
            <p className="mt-3 font-caption text-muted-foreground">No accounts found matching your filters.</p>
          </div>
        )}
      </div>
    </>
  );
}